import { spacing } from '@scality/core-ui';
import { Accordion, Box, useChartLegend } from '@scality/core-ui/dist/next';
import { useCallback, useId, useRef, useState } from 'react';
import styled from 'styled-components';
import type { LegendInstance } from '../../../portal/instanceMarker';
import { ChartInstanceLegend } from './ChartInstanceLegend';

/** Rows shown before the list starts scrolling — past that the legend buries the next chart. */
const VISIBLE_LEGEND_ROWS = 3;
/** core-ui leaves 16px under a row of legend items, which buys nothing above a 175px plot. */
const LEGEND_ROW_GAP = spacing.r8;
/** A `Smaller` line box plus the gap under it. */
const LEGEND_ROW_HEIGHT = `calc(1rem + ${LEGEND_ROW_GAP})`;
const MAX_LEGEND_HEIGHT = `calc(${VISIBLE_LEGEND_ROWS} * ${LEGEND_ROW_HEIGHT})`;

/**
 * core-ui's Accordion is sized for page sections, which is heavy above a 175px plot; it takes no
 * className, so its chrome is trimmed from the outside.
 */
const CompactAccordion = styled.div`
  /* No side padding on the box: the scrollbar then sits against the border rather than
     floating inside it, and the rows carry their own inset. */
  > div {
    padding: 0;
    padding-top: ${spacing.r8};
  }

  h3 button {
    padding: 0 ${spacing.r8} ${spacing.r8};
    margin-bottom: 0;
  }

  h3 button i {
    font-size: 0.75rem;
  }

  h3 button span {
    font-size: 0.75rem;
  }
`;

/**
 * Neither gap core-ui draws inside its legend takes a prop, so both are trimmed from the outside:
 * the rows close up, and a swatch sits nearer the label it belongs to.
 */
const TightLegend = styled(Box)`
  > div {
    row-gap: ${LEGEND_ROW_GAP};
  }

  > div > div {
    gap: ${spacing.r4};
  }
`;

type ChartLegendPanelProps = {
  /** Keyed by the series name the chart knows, which is what the legend lists. */
  instances?: Record<string, LegendInstance>;
};

/**
 * The chart legend, under the plot in every view.
 *
 * It is kept inside a bounded frame of its own: a per-node series list runs to dozens of entries,
 * and left to its natural height it buries the charts below it. Frame and collapse control only
 * appear once the list overflows, so a two-series chart keeps a bare legend.
 */
export const ChartLegendPanel = ({ instances = {} }: ChartLegendPanelProps) => {
  const { listResources } = useChartLegend();
  const resources = listResources();
  const accordionId = useId();
  const [isOverflowing, setIsOverflowing] = useState(false);
  const observerRef = useRef<ResizeObserver | null>(null);

  // Item widths are data-dependent (bare node ids, of any length), so overflow is measured rather
  // than guessed from the series count. A callback ref, so collapsing and re-expanding re-attaches.
  const measureOverflow = useCallback((scroller: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    const legend = scroller?.firstElementChild;
    if (!scroller || !legend) return;

    const observer = new ResizeObserver(() => setIsOverflowing(legend.scrollHeight > scroller.clientHeight));
    observer.observe(legend);
    observerRef.current = observer;
  }, []);

  const renderScrollingLegend = (isFramed: boolean) => (
    <Box ref={measureOverflow} maxHeight={MAX_LEGEND_HEIGHT} overflowY="auto">
      {/* The inset lives here rather than on the scroll box, which has to reach the frame's edge. */}
      <TightLegend padding={isFramed ? `0 ${spacing.r8}` : 0}>
        <ChartInstanceLegend
          shape="rectangle"
          direction="horizontal"
          size="Smaller"
          color="textSecondary"
          instances={instances}
        />
      </TightLegend>
    </Box>
  );

  return (
    <Box marginTop={spacing.r8}>
      {isOverflowing ? (
        <CompactAccordion>
          <Accordion id={accordionId} title={`Legend (${resources.length})`} open isEmphazed={false}>
            {renderScrollingLegend(true)}
          </Accordion>
        </CompactAccordion>
      ) : (
        renderScrollingLegend(false)
      )}
    </Box>
  );
};
